// Sentry — browser error reporting.
//
// Mirrors server/core/sentry.js: errors only, no tracing, no session replay.
// With no VITE_SENTRY_DSN the SDK never initialises and every export is a
// no-op, so dev, `vitest` and any build without the var behave as before.
//
// The DSN is compiled into the public bundle. That is correct and expected for
// Sentry — a DSN is a write-only ingest key, it cannot read your data. (Worth
// stating explicitly in this repo, where VITE_KIOSK_SECRET taught everyone to
// be suspicious of VITE_-prefixed secrets.)
//
// Release + environment are injected at build time by vite.config.js rather
// than read from Railway vars, so nobody has to hand-maintain a version string
// in a dashboard. The release MUST match the server's for source maps to
// resolve — both derive from the repo-root VERSION file.

import * as Sentry from '@sentry/react';

let enabled = false;

/**
 * Drop anything that could carry employee PII. The browser SDK is less
 * dangerous than the server's (no request bodies), but breadcrumbs capture
 * fetch URLs, and this app's URLs carry employee and building ids.
 */
function scrub(event) {
  delete event.user;
  if (event.request) {
    delete event.request.cookies;
    delete event.request.headers;
    if (typeof event.request.url === 'string') {
      event.request.url = event.request.url.split('?')[0];
    }
  }
  return event;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_APP_RELEASE || undefined,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend: scrub,
    // Browser extensions and injected scripts throw constantly in other
    // people's code. Without this the issue stream is mostly noise from
    // whatever the warehouse tablets have installed.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  });

  enabled = true;
  return true;
}

export function isSentryEnabled() {
  return enabled;
}

/**
 * Report a caught render error. Called by ErrorBoundary, which previously
 * only console.error'd — see the TODO this replaces.
 *
 * @param {Error} error
 * @param {{componentStack?: string}} [info] React error info
 */
export function captureClientError(error, info) {
  if (!enabled) return null;
  return Sentry.captureException(error, {
    contexts: info?.componentStack
      ? { react: { componentStack: info.componentStack } }
      : undefined,
  });
}
